/**
 * useMapSelection hook.
 *
 * Manages the currently selected entity (field, tree, farmer, etc.)
 * and synchronizes selection between the map and the drawer.
 */

import { useState, useCallback } from 'react';
import type { MapField, MapTree, MapFarmer, MapCohort, MapWarehouse, GeoCoord } from '../types/mapTypes';

export type SelectedEntityType = 'field' | 'tree' | 'farmer' | 'cohort' | 'warehouse' | null;

export interface SelectionState {
  type: SelectedEntityType;
  entity: MapField | MapTree | MapFarmer | MapCohort | MapWarehouse | null;
}

interface UseMapSelectionReturn {
  selection: SelectionState;
  selectField: (field: MapField) => void;
  selectTree: (tree: MapTree) => void;
  selectFarmer: (farmer: MapFarmer) => void;
  selectCohort: (cohort: MapCohort) => void;
  selectWarehouse: (warehouse: MapWarehouse) => void;
  clearSelection: () => void;
  focusCoordinate: GeoCoord | null;
  focusZoom: number;
}

const INITIAL: SelectionState = { type: null, entity: null };

export function useMapSelection(): UseMapSelectionReturn {
  const [selection, setSelection] = useState<SelectionState>(INITIAL);
  const [focusCoordinate, setFocusCoordinate] = useState<GeoCoord | null>(null);
  const [focusZoom, setFocusZoom] = useState(15);

  const selectField = useCallback((field: MapField) => {
    setSelection({ type: 'field', entity: field });
    const center = field.center || (field.boundary.length > 0 ? field.boundary[0] : null);
    if (center) {
      setFocusCoordinate(center);
      setFocusZoom(15);
    }
  }, []);

  const selectTree = useCallback((tree: MapTree) => {
    setSelection({ type: 'tree', entity: tree });
    setFocusCoordinate(tree.position);
    setFocusZoom(19);
  }, []);

  const selectFarmer = useCallback((farmer: MapFarmer) => {
    setSelection({ type: 'farmer', entity: farmer });
    const center = farmer.location || (farmer.boundary.length > 0 ? farmer.boundary[0] : null);
    if (center) {
      setFocusCoordinate(center);
      setFocusZoom(15);
    }
  }, []);

  const selectCohort = useCallback((cohort: MapCohort) => {
    setSelection({ type: 'cohort', entity: cohort });
    if (cohort.boundary.length > 0) {
      // Compute centroid
      let sumLng = 0, sumLat = 0;
      for (const [lng, lat] of cohort.boundary) {
        sumLng += lng;
        sumLat += lat;
      }
      setFocusCoordinate([sumLng / cohort.boundary.length, sumLat / cohort.boundary.length]);
      setFocusZoom(13);
    }
  }, []);

  const selectWarehouse = useCallback((warehouse: MapWarehouse) => {
    setSelection({ type: 'warehouse', entity: warehouse });
    setFocusCoordinate(warehouse.position);
    setFocusZoom(16);
  }, []);

  const clearSelection = useCallback(() => {
    setSelection(INITIAL);
    setFocusCoordinate(null);
  }, []);

  return {
    selection,
    selectField,
    selectTree,
    selectFarmer,
    selectCohort,
    selectWarehouse,
    clearSelection,
    focusCoordinate,
    focusZoom,
  };
}
